'use client';

import { useCallback, useEffect, useState } from 'react';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let listenersInitialized = false;
const subscribers = new Set<() => void>();

function notifySubscribers() {
  subscribers.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function detectMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    detectIOS() ||
    /Android/i.test(navigator.userAgent) ||
    window.matchMedia('(max-width: 768px)').matches
  );
}

function initPwaInstallListeners() {
  if (listenersInitialized || typeof window === 'undefined') return;
  listenersInitialized = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notifySubscribers();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notifySubscribers();
  });
}

export interface PwaInstallState {
  isInstalled: boolean;
  isIOS: boolean;
  isMobile: boolean;
  isInstallable: boolean;
  /** Show install UI (not already installed). */
  canShowInstall: boolean;
  promptInstall: () => Promise<boolean>;
}

export function usePwaInstall(): PwaInstallState {
  const [, bump] = useState(0);

  useEffect(() => {
    initPwaInstallListeners();
    return subscribe(() => bump((n) => n + 1));
  }, []);

  const isInstalled = detectStandalone();
  const isIOS = detectIOS();
  const isMobile = detectMobile();
  const isInstallable = deferredPrompt !== null;
  const canShowInstall = !isInstalled;

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    notifySubscribers();
    return outcome === 'accepted';
  }, []);

  return {
    isInstalled,
    isIOS,
    isMobile,
    isInstallable,
    canShowInstall,
    promptInstall,
  };
}
